import { formatDate } from '../../components/data-grid/format';
import { formatMoney } from '../../components/data-grid/money';
import { sheetOf } from '../../components/report-grid/export';
import { ReportGrid } from '../../components/report-grid/ReportGrid';
import type { ReportGridColumn } from '../../components/report-grid/ReportGrid';
import { useI18n } from '../../i18n/I18nProvider';
import type { Language } from '../../i18n/catalogs';
import type { AccountCardReport, AccountCardRow } from './reports.api';
import { ReportFrame } from './ReportFrame';
import { reportSheetName } from './totals';

export interface AccountCardViewProps {
  report: AccountCardReport;
  language: Language;
  periodLabel: string;
  accountLabel: string;
}

export function AccountCardView({
  report,
  language,
  periodLabel,
  accountLabel,
}: AccountCardViewProps): JSX.Element {
  const { t } = useI18n();

  const columns: Array<ReportGridColumn<AccountCardRow>> = [
    { key: 'date', header: t('reportsDate'), render: (row) => formatDate(row.date), exportValue: (row) => row.date },
    {
      key: 'document',
      header: t('reportsDocument'),
      render: (row) => (
        <a className="report__link" href={`/documents/${row.documentId}`}>
          {row.documentType}
        </a>
      ),
      exportValue: (row) => row.documentType,
    },
    {
      key: 'description',
      header: t('reportsDescription'),
      render: (row) => row.description ?? '',
      exportValue: (row) => row.description ?? '',
    },
    {
      key: 'debit',
      header: t('reportsDebit'),
      align: 'right',
      numeric: true,
      render: (row) => (row.debit === '0.0000' ? '—' : formatMoney(row.debit, language)),
      exportValue: (row) => row.debit,
    },
    {
      key: 'credit',
      header: t('reportsCredit'),
      align: 'right',
      numeric: true,
      render: (row) => (row.credit === '0.0000' ? '—' : formatMoney(row.credit, language)),
      exportValue: (row) => row.credit,
    },
    {
      key: 'balance',
      header: t('reportsBalance'),
      align: 'right',
      numeric: true,
      render: (row) => formatMoney(row.balance, language),
      exportValue: (row) => row.balance,
    },
  ];

  const sheet = sheetOf(
    reportSheetName('account-card', report),
    columns.map((column) => ({
      header: column.header,
      numeric: column.numeric,
      exportValue: column.exportValue,
    })),
    report.rows,
    ['', '', '', report.turnoverDt, report.turnoverCt, report.closing],
  );

  return (
    <ReportFrame
      title={t('reportsAccountCard')}
      subtitle={accountLabel}
      periodLabel={periodLabel}
      sheet={sheet}
    >
      <div className="report__metrics">
        <article className="metric">
          <span className="metric__label">{t('reportsOpeningBalance')}</span>
          <span className="metric__value">{formatMoney(report.opening, language)}</span>
        </article>
        <article className="metric">
          <span className="metric__label">{t('reportsTurnover')}</span>
          <span className="metric__value">
            {formatMoney(report.turnoverDt, language)} / {formatMoney(report.turnoverCt, language)}
          </span>
        </article>
        <article className="metric" data-testid="account-card-closing">
          <span className="metric__label">{t('reportsClosingBalance')}</span>
          <span className="metric__value">{formatMoney(report.closing, language)}</span>
        </article>
      </div>

      <ReportGrid
        columns={columns}
        rows={report.rows}
        getRowKey={(row) => row.lineId}
        totals={[
          t('reportsTotals'),
          '',
          '',
          formatMoney(report.turnoverDt, language),
          formatMoney(report.turnoverCt, language),
          formatMoney(report.closing, language),
        ]}
        emptyText={t('reportsEmpty')}
        testId="account-card"
      />
    </ReportFrame>
  );
}
